import React, { useState, useEffect, useContext } from 'react';
import api, { clearStoredSession, getUserFacingErrorMessage, isAuthError } from '../utils/api';
import { UserContext } from '../App';
import OwnerShell from './OwnerShell';
import { useNavigate, useLocation } from 'react-router-dom';
import ContextSwitcher from './ContextSwitcher';
import NomineeRequiredBanner from './NomineeRequiredBanner';
import ResponsiveOptionNav from './ResponsiveOptionNav';
import { Button, Modal } from '../ui';

export const FINANCIAL_CATEGORIES = [
  'Bank account',
  'Credit Card',
  'Investment account',
  'Pension',
  'Insurance',
  'Property',
  'Utilities'
];

const TYPE_OPTIONS = {
  'Bank account': ['Savings', 'Checking', 'Mortgage'],
  'Credit Card': ['Credit Card'],
  'Investment account': ['Cash ISA', 'Stocks & Shares ISA', 'Trading'],
  'Pension': ['managed', 'SIPP'],
  'Insurance': ['Home', 'Contents', 'Car', 'Phone', 'Travel'],
  'Property': ['Residential', 'Commercial', 'Rental'],
  'Utilities': ['Water', 'Gas', 'Electricity', 'TV', 'Broadband', 'Mobile phone', 'Home phone']
};

const INSTITUTION_OPTIONS = {
  'Bank account': ['NatWest', 'Lloyds Bank', 'Nationwide', 'Halifax', 'HSBC', 'Monzo', 'Barclays'],
  'Credit Card': ['American Express', 'NatWest', 'Lloyds Bank', 'Nationwide', 'Halifax', 'HSBC', 'Barclays'],
  'Investment account': ['ii', 'Hargreaves Landsdown', 'Barclays', 'AJ Bell', 'IG Group', 'Aviva'],
  'Pension': ['ii', 'Scottish Widows', 'Smart Pension'],
  'Insurance': ['Compare the Market', 'Aviva', 'Admiral', 'LV'],
  'Property': [], // free form
  'Utilities': {
    'Water': ['Thames Water', 'Severn Trent', 'United Utilities', 'Yorkshire Water', 'Anglian Water', 'Scottish Water', 'Welsh Water (Dwr Cymru)', 'Southern Water', 'Northumbrian Water', 'South West Water'],
    'Gas': ['Octopus Energy', 'British Gas', 'E.ON Next', 'EDF Energy', 'Scottish Power', 'OVO Energy', 'SSE'],
    'Electricity': ['Octopus Energy', 'British Gas', 'E.ON Next', 'EDF Energy', 'Scottish Power', 'OVO Energy', 'SSE'],
    'TV': ['Sky', 'Virgin Media', 'BT TV', 'NOW'],
    'Broadband': ['BT', 'Sky', 'Virgin Media', 'TalkTalk', 'Plusnet', 'Vodafone', 'EE', 'Zen Internet', 'Hyperoptic', 'Community Fibre'],
    'Mobile phone': ['EE', 'O2', 'Vodafone', 'Three', 'giffgaff', 'Tesco Mobile', 'Sky Mobile', 'VOXI', 'SMARTY', 'Lebara'],
    'Home phone': ['BT', 'Sky', 'Virgin Media', 'TalkTalk', 'Plusnet', 'Vodafone']
  }
};

export default function FinancialAssets() {
  const { currentUser, users, setCurrentUser } = useContext(UserContext);
  const [assets, setAssets] = useState([]);
  const [hasNominees, setHasNominees] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [form, setForm] = useState({ type: '', typeCategory: '', institution: '', accountNo: '', value: '', notes: '' });
  const [activeCategory, setActiveCategory] = useState('Bank account');
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const isInsurance = activeCategory === 'Insurance';
  // Hide account/value for categories where they are either not meaningful or not required.
  // This keeps the UI focused and avoids empty columns.
  const HIDE_ACCOUNT_VALUE_CATEGORIES = ['Bank account', 'Credit Card', 'Investment account', 'Pension', 'Utilities'];
  const hideAccountAndValue = HIDE_ACCOUNT_VALUE_CATEGORIES.includes(activeCategory);

  // Asset owner is the current logged-in user
  const assetHolder = currentUser;
  if (!assetHolder) {
    return <div>Loading...</div>;
  }

  // Nominee journey now lives under /nominee.
  useEffect(() => {
    if (currentUser?.type === 'nominee') {
      navigate('/nominee');
    }
  }, [currentUser?.type, navigate]);

  if (currentUser?.type === 'nominee') {
    return <div style={{ padding: 20 }}>Redirecting…</div>;
  }

  // Fetch nominee presence (owner-scoped) to decide whether to allow asset mutations.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/nominees');
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setHasNominees(list.length > 0);
      } catch (err) {
        if (cancelled) return;
        console.error('FinancialAssets: failed to load nominees', err?.response?.data || err);
        if (isAuthError(err)) {
          clearStoredSession();
          setCurrentUser(null);
        } else {
          // If we can't confirm, defer to server enforcement.
          setHasNominees(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [setCurrentUser]);

  // Fetch financial assets for asset holder (owner view)
  useEffect(() => {
    if (currentUser.type === 'owner') {
      let cancelled = false;
      (async () => {
        try {
          const res = await api.get('/assets');
          if (cancelled) return;
          const all = Array.isArray(res.data) ? res.data : [];
          const holderAssets = all.filter(a => a.ownerId === assetHolder.id && FINANCIAL_CATEGORIES.includes(a.typeCategory));
          setAssets(holderAssets);
          if (holderAssets.length > 0) {
            const firstCat = FINANCIAL_CATEGORIES.find(cat => holderAssets.some(a => a.typeCategory === cat));
            setActiveCategory(firstCat || FINANCIAL_CATEGORIES[0]);
          } else {
            setActiveCategory(FINANCIAL_CATEGORIES[0]);
          }
        } catch (err) {
          if (cancelled) return;
          console.error('FinancialAssets: failed to load assets', err?.response?.data || err);
          if (isAuthError(err)) {
            clearStoredSession();
            setCurrentUser(null);
          }
        }
      })();
      return () => { cancelled = true; };
    }
  }, [currentUser, assetHolder.id]);

  // Note: nominee views were removed from this screen in favour of /nominee/* pages.

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  // Step logic
  const resetForm = () => {
    setForm({ type: '', typeCategory: '', institution: '', accountNo: '', value: '', notes: '' });
    setStep(0);
    setEditingAssetId(null);
  };

  const refreshAssets = async () => {
    const res = await api.get('/assets');
    setAssets(res.data.filter(a => a.ownerId === assetHolder.id && FINANCIAL_CATEGORIES.includes(a.typeCategory)));
  };

  const openModal = (assetToEdit = null) => {
    if (hasNominees === false) {
      navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
      return;
    }
    console.debug('opening financial asset modal');
    setModalOpen(true);
    if (assetToEdit) {
      setEditingAssetId(assetToEdit.id);
      setForm({
        type: assetToEdit.type || '',
        typeCategory: assetToEdit.typeCategory || '',
        institution: assetToEdit.institution || '',
        accountNo: assetToEdit.accountNo || '',
        value: assetToEdit.value == null ? '' : String(assetToEdit.value),
        notes: assetToEdit.notes || '',
      });
      // Start on details; user can go Back to change category/type/institution.
      setStep(3);
    } else {
      resetForm();
    }
  };
  const closeModal = () => { setModalOpen(false); resetForm(); };

  const handleCategorySelect = e => {
    // Clear downstream fields when switching category to avoid stale values.
    setForm(f => ({ ...f, typeCategory: e.target.value, type: '', institution: '', accountNo: '', value: '', notes: '' }));
    setStep(1);
  };
  const handleTypeSelect = e => {
    setForm(f => ({ ...f, type: e.target.value }));
    setStep(2);
  };
  const handleInstitutionSelect = e => {
    setForm(f => ({ ...f, institution: e.target.value }));
    setStep(3);
  };
  const handleFreeInstitution = e => {
    setForm(f => ({ ...f, institution: e.target.value }));
  };
  const handleAccountNo = e => setForm(f => ({ ...f, accountNo: e.target.value }));
  const handleValue = e => setForm(f => ({ ...f, value: e.target.value }));

  const saveAsset = async e => {
    e.preventDefault();
    try {
      const payload = { ...form, ownerId: assetHolder.id, notes: form.notes || '' };
      if (editingAssetId) {
        await api.patch(`/assets/${encodeURIComponent(editingAssetId)}`, payload);
      } else {
        await api.post('/assets', payload);
      }
      await refreshAssets();
      closeModal();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (err?.response?.status === 409 && code === 'NOMINEE_REQUIRED') {
        alert(getUserFacingErrorMessage(err, {
          fallback: 'Add at least one nominee before you can add assets.',
          includeSupportForUnknown: false,
        }));
        navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
        return;
      }
      alert(getUserFacingErrorMessage(err, {
        fallback: 'We could not save that asset. Check your entries and try again.',
        includeSupportForUnknown: false,
      }));
    }
  };

  const deleteAsset = async (asset) => {
    if (!asset?.id) return;
    const ok = window.confirm('Delete this asset? This cannot be undone.');
    if (!ok) return;
    try {
      await api.delete(`/assets/${encodeURIComponent(asset.id)}`);
      await refreshAssets();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (err?.response?.status === 409 && code === 'NOMINEE_REQUIRED') {
        alert(getUserFacingErrorMessage(err, {
          fallback: 'Add at least one nominee before you can change assets.',
          includeSupportForUnknown: false,
        }));
        navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
        return;
      }
      alert(getUserFacingErrorMessage(err, {
        fallback: 'We could not delete that asset. Please try again.',
        includeSupportForUnknown: false,
      }));
    }
  };

  return (
    <OwnerShell>
      <>
        <div style={{ padding: 20, position: 'relative' }}>
          {currentUser.type === 'owner' && hasNominees === false && (
            <NomineeRequiredBanner returnTo={location.pathname} />
          )}
          {currentUser.type === 'nominee' ? (
            <>
              {/* Owner selector dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <h1 style={{ margin: 0 }}>
                  {(() => {
                    const donor = beneficiaryOwners.find(o => o.ownerId === selectedOwnerId);
                    if (!donor) return 'Owner\'s Financial Assets';
                    let profile = {};
                    try { profile = JSON.parse(localStorage.getItem('ownerProfile') || '{}'); } catch {}
                    const displayName = (profile.firstName && profile.lastName)
                      ? `${profile.firstName} ${profile.lastName}`
                      : (donor.ownerName || 'Owner');
                    return `${displayName}'s Financial Assets`;
                  })()}
                </h1>
                <select
                  value={selectedOwnerId}
                  onChange={e => setSelectedOwnerId(e.target.value)}
                  style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16, minWidth: 180 }}
                >
                  {beneficiaryOwners.map(o => (
                    <option key={o.ownerId} value={o.ownerId}>{(() => {
                      let profile = {};
                      try { profile = JSON.parse(localStorage.getItem('ownerProfile') || '{}'); } catch {}
                      return (profile.firstName && profile.lastName)
                        ? `${profile.firstName} ${profile.lastName}`
                        : (o.ownerName || o.ownerId);
                    })()}</option>
                  ))}
                </select>
              </div>
              {/* Category Tabs */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 24, marginTop: 8, flexWrap: 'wrap' }}>
                {FINANCIAL_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 12,
                      border: 'none',
                      background: activeCategory === cat ? 'var(--sage-600)' : 'var(--mist-200)',
                      color: activeCategory === cat ? '#fff' : 'var(--dusk-700)',
                      fontWeight: activeCategory === cat ? 600 : 400,
                      fontSize: 15,
                      cursor: 'pointer',
                      boxShadow: activeCategory === cat ? 'var(--shadow-soft)' : 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              {beneficiaryAssets.filter(a => a.typeCategory === activeCategory).length === 0 ? (
                <div style={{ marginTop: 60, textAlign: 'center', color: '#888' }}>
                  <p style={{ fontSize: 20 }}>No financial assets in this category yet.</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Institution</th>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>{isInsurance ? 'Policy / Ref' : 'Account / Ref'}</th>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Value</th>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {beneficiaryAssets.filter(a => a.typeCategory === activeCategory).map(a => (
                      <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px 8px' }}>{a.type}</td>
                        <td style={{ padding: '10px 8px' }}>{a.institution}</td>
                        <td style={{ padding: '10px 8px' }}>{a.accountNo || '-'}</td>
                        <td style={{ padding: '10px 8px' }}>
                          {a.value == null || a.value === ''
                            ? '-'
                            : `$${Number(a.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        </td>
                        <td style={{ padding: '10px 8px' }}>{a.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                <h1 style={{ margin: 0 }}>Financial Assets</h1>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <ContextSwitcher />
                  <button
                    onClick={() => openModal()}
                    aria-disabled={hasNominees === false}
                    title={hasNominees === false ? 'Add a nominee first' : 'Add a financial asset'}
                    style={{
                      padding: '10px 18px',
                      fontSize: 16,
                      background: hasNominees === false ? '#8fa8a0' : 'var(--sage-600)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      cursor: 'pointer',
                      boxShadow: 'var(--shadow-soft)',
                      fontWeight: 600,
                      opacity: hasNominees === false ? 0.85 : 1,
                      maxWidth: '100%',
                    }}
                  >
                    Add financial asset
                  </button>
                </div>
              </div>
              {/* Category Tabs */}
              <ResponsiveOptionNav
                label="Category"
                options={FINANCIAL_CATEGORIES}
                value={activeCategory}
                onChange={setActiveCategory}
              />
              {assets.filter(a => a.typeCategory === activeCategory).length === 0 ? (
                <div style={{ marginTop: 60, textAlign: 'center', color: '#888' }}>
                  <p style={{ fontSize: 20 }}>No financial assets in this category yet.</p>
                  <p>Click <b>Add financial asset</b> to get started.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto', borderRadius: 8 }}>
                  <table style={{ width: '100%', minWidth: hideAccountAndValue ? 620 : 760, borderCollapse: 'collapse', marginTop: 20, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Type</th>
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Institution</th>
                        {!hideAccountAndValue && (
                          <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>{isInsurance ? 'Policy / Ref' : 'Account / Ref'}</th>
                        )}
                        {!hideAccountAndValue && (
                          <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Value</th>
                        )}
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Notes</th>
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assets.filter(a => a.typeCategory === activeCategory).map(a => (
                        <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '10px 8px' }}>{a.type}</td>
                          <td style={{ padding: '10px 8px' }}>{a.institution}</td>
                          {!hideAccountAndValue && (
                            <td style={{ padding: '10px 8px' }}>{a.accountNo || '-'}</td>
                          )}
                          {!hideAccountAndValue && (
                            <td style={{ padding: '10px 8px' }}>
                              {a.value == null || a.value === ''
                                ? '-'
                                : `$${Number(a.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            </td>
                          )}
                          <td style={{ padding: '10px 8px' }}>{a.notes}</td>
                          <td style={{ padding: '10px 8px' }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <Button type="button" variant="primary" onClick={() => openModal(a)}>
                                Edit
                              </Button>
                              <Button type="button" variant="danger" onClick={() => deleteAsset(a)}>
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
        {/* Modal for asset holder only */}
        {currentUser.type === 'owner' && (
          <Modal
            open={modalOpen}
            onClose={closeModal}
            title={editingAssetId ? 'Edit Financial Asset' : 'Add Financial Asset'}
            subtitle={editingAssetId ? 'Update the details of your financial asset.' : 'Enter the details of your financial asset below.'}
          >
            <form onSubmit={saveAsset} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {step === 0 && (
                <div style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>Asset Category<br />
                    <select name="typeCategory" value={form.typeCategory} onChange={handleCategorySelect} required style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }}>
                      <option value="" disabled>Select category</option>
                      {FINANCIAL_CATEGORIES.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </label>
                </div>
              )}
              {step === 1 && (
                <div style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>Asset Type<br />
                    <select name="type" value={form.type} onChange={handleTypeSelect} required style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }}>
                      <option value="" disabled>Select type</option>
                      {TYPE_OPTIONS[form.typeCategory]?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </label>
                  <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    <Button type="button" variant="secondary" onClick={() => setStep(0)}>
                      Back
                    </Button>
                  </div>
                </div>
              )}
              {step === 2 && (
                <div style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>Institution<br />
                    {form.typeCategory === 'Property' ? (
                      <input name="institution" placeholder="Institution" value={form.institution} onChange={handleFreeInstitution} required style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }} />
                    ) : (
                      <select name="institution" value={form.institution} onChange={handleInstitutionSelect} required style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }}>
                        <option value="" disabled>Select institution</option>
                        {Array.isArray(INSTITUTION_OPTIONS[form.typeCategory])
                          ? INSTITUTION_OPTIONS[form.typeCategory].map(opt => <option key={opt} value={opt}>{opt}</option>)
                          : (INSTITUTION_OPTIONS[form.typeCategory]?.[form.type] || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    )}
                  </label>
                  <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    <Button type="button" variant="secondary" onClick={() => setStep(1)}>
                      Back
                    </Button>
                  </div>
                </div>
              )}
              {step === 3 && (
                <>
                  {!hideAccountAndValue && (
                    <div style={{ marginBottom: 0 }}>
                      <label style={{ fontWeight: 500, color: '#333' }}>{form.typeCategory === 'Insurance' ? 'Policy / account reference (optional)' : 'Account / reference (optional)'}<br />
                        <input
                          name="financial_accountNo"
                          autoComplete="off"
                          autoCorrect="off"
                          spellCheck={false}
                          placeholder={form.typeCategory === 'Insurance' ? 'Policy number (optional)' : 'Account number / reference (optional)'}
                          value={form.accountNo}
                          onChange={handleAccountNo}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }}
                        />
                      </label>
                    </div>
                  )}
                  {!hideAccountAndValue && (
                    <div style={{ marginBottom: 0 }}>
                      <label style={{ fontWeight: 500, color: '#333' }}>Value (optional)<br />
                        <input
                          name="financial_value"
                          autoComplete="off"
                          placeholder="e.g., 1500"
                          inputMode="decimal"
                          type="number"
                          value={form.value}
                          onChange={handleValue}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }}
                        />
                      </label>
                    </div>
                  )}
                  <div style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 500, color: '#333' }}>Details / notes (optional)<br />
                      <textarea
                        name="financial_notes"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        placeholder="Any extra details (optional)…"
                        value={form.notes}
                        onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6, minHeight: 90, resize: 'vertical' }}
                      />
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                    <Button type="button" variant="secondary" onClick={() => setStep(2)}>
                      Back
                    </Button>
                    <Button type="submit" variant="primary" style={{ flex: 1 }}>
                      {editingAssetId ? 'Save changes' : 'Save'}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </Modal>
        )}
      </>
    </OwnerShell>
  );
} 